/* eslint-disable better-mutation/no-mutation */

import React from 'react';
import Axios from 'axios';
import * as blueshift from '../../../helpers/blueshift';
import * as ga from '../../../services/login-signup-analytics';
import GoogleTagManager from '../../../services/google-tag-manager';
import ErrorMessages from '../../../messages/error-messages';
import BlockingLoader from '../../common/form/blocking-loader';
import PersonalDetailsAutoFill from '../swedish-private-v2/personal-details-autofill';
import Form from '../swedish-private-v2/form';
import LocalFormStore from '../swedish-private-v2/local-form-store';
import Google from '../../../helpers/social-auth/google';
import Apple from '../../../helpers/social-auth/apple';
import Facebook from '../../../helpers/social-auth/facebook';
import Klarna from '../../../helpers/social-auth/klarna';
import { FacebookProfileIcon } from '../../icons';
import { SwedishPrivateProviderEmailRequired } from './swedish-private-provider-email-required';
import { SwedishPrivateProviderEmailExists } from './swedish-private-provider-email-exists';
import { SwedishPrivateProviderIdExists } from './swedish-private-provider-id-exists';
import { SwedishPrivateFormAccountCreatedWithProvider } from './swedish-private-form-account-created-with-provider';
import { SwedishPrivateFormStep1 } from './swedish-private-form-step-1';
import { SwedishPrivateFormStep2 } from './swedish-private-form-step-2';
import { SwedishPrivateFacebook } from './swedish-private-facebook';
import { SwedishPrivateApple } from './swedish-private-apple';
import { SwedishPrivateGoogle } from './swedish-private-google';
import { SwedishPrivateKlarna } from './swedish-private-klarna';
import { SwedishPrivateManualReview } from './swedish-private-manual-review';
import { SwedishPrivateWaitForAccountCreation } from './swedish-private-wait-for-account-creation';
import { SwedishPrivateFormAccountCreated } from './swedish-private-form-account-created';
import { isServer } from 'tradera/utils/nextjs';
import {
    FUNNEL_APPLE,
    FUNNEL_FACEBOOK,
    FUNNEL_GOOGLE,
    FUNNEL_KLARNA,
    FUNNEL_PASSWORD,
    PROVIDERS,
    PROVIDER_NAMES
} from 'tradera/apps/registration/helpers/social-auth/providers';
import { waitForCountSessionId } from 'tradera/apps/registration/components/common/kount-session-id-awaiter';

if (isServer) {
    // Do not remove this without also removing the disable of eslint better-mutation/no-mutation at top of file.
    throw new Error(
        'This implementation may not compatible with SSR where global properties are shared between server instances'
    );
}

export class SwedishPrivateForm extends React.Component {
    constructor(props) {
        super(props);
        this.autoFill = new PersonalDetailsAutoFill();
        this.formStore = new LocalFormStore();
        this.facebook = new Facebook(props.facebookAppId);
        this.apple = new Apple(props.appleAppId);
        this.google = new Google(props.googleAppId);
        this.klarna = new Klarna(
            props.klarnaAppId,
            props.klarnaEnvironment,
            'sv-SE',
            (res) => this.klarnaAuthenticated(res)
        );
        this.funnel = null;

        this.state = {
            step: '/step/1',
            acceptsTerms: false,
            termsError: false,
            form: new Form({
                EmailAddress: {
                    rules: 'copyfor=email:required;email;minlength[5];maxlength[255]',
                    async: '/register/api/validate/email'
                },
                PersonalNumber: {
                    rules: 'copyfor=personalid:required;personalid',
                    async: '/register/api/validate/personal-number'
                },
                Alias: {
                    rules: 'copyfor=alias:required;minlength[2];maxlength[50];latinwithnumbers',
                    async: '/register/api/validate/alias'
                },
                FacebookId: {
                    rules: '',
                    async: '/register/api/validate/facebook-id'
                },
                PhoneNumber: {
                    rules: 'copyfor=phonenumber:required;phone;minlength[8];maxlength[15]'
                },
                FirstName: {
                    rules: 'copyfor=firstname:required;minlength[2];maxlength[30];latinonly'
                },
                LastName: {
                    rules: 'copyfor=lastname:required;minlength[2];maxlength[30];latinonly'
                },
                StreetName: {
                    rules: 'copyfor=streetname:required;latinwithnumbers'
                },
                ZipCode: {
                    rules: 'copyfor=zipcode:required;zipcode'
                },
                Town: {
                    rules: 'copyfor=town:required;latinonly'
                },
                FacebookFirstName: {
                    rules: ''
                }
            })
        };

        this.setStepFromLocationHash = this.setStepFromLocationHash.bind(this);
        this.forceFormUpdate = this.forceFormUpdate.bind(this);
    }

    componentDidMount() {
        Axios.defaults.headers.common['X-Xsrf-Token'] =
            this.props.antiForgeryToken;

        this.formStore.restoreState(this.state.form);
        this.state.form.onChange(this.forceFormUpdate);
        this.state.form.onChange(this.formStore.saveState);

        if (this.props.autoFillFeatureEnabled) {
            this.state.form.onValidation(this.autoFill.onValidation);
        }

        this.initializeLocation();

        ga.method.setIfEmpty(ga.METHOD.UNKNOWN);
        ga.flow.set(ga.FLOW.SIGNUP);
        ga.trackEvent('user-signup-step-1');

        this.state.form.onValidation((form, field) => {
            const fieldState = form.fields[field];
            if (
                fieldState.isValid ||
                !fieldState.errors ||
                fieldState.errors.length === 0
            ) {
                return;
            }
            fieldState.errors.forEach((e) => {
                if (e.async && e.code === 'ALIAS_IN_USE') {
                    ga.trackEvent('user-signup-fail', 0, 'alias-exists');
                } else if (e.async && e.code === 'INVALID_PERSONALID') {
                    ga.trackEvent(
                        'user-signup-fail',
                        0,
                        'invalid-personnummer'
                    );
                } else {
                    // ga.trackEvent('user-signup-fail', 0, 'other');
                }
            });
        });

        if (this.props.isNativeAppContext) {
            this.facebook.onNativeInitiatedLogin((facebookAuthData) => {
                if (facebookAuthData) {
                    ga.method.set(ga.METHOD.FACEBOOK);
                    ga.trackEvent('user-signup-step-2');

                    this.setState({ isLoading: true });
                    this.facebookAuthenticated(facebookAuthData);
                }
            });
            this.apple.onNativeInitiatedLogin((appleAuthData) => {
                if (appleAuthData) {
                    ga.method.set(ga.METHOD.APPLE);
                    ga.trackEvent('user-signup-step-2');

                    this.setState({ isLoading: true });
                    this.appleAuthenticated(appleAuthData);
                }
            });
            this.google.onNativeInitiatedLogin((googleAuthData) => {
                if (googleAuthData) {
                    ga.method.set(ga.METHOD.GOOGLE);
                    ga.trackEvent('user-signup-step-2');

                    this.setState({ isLoading: true });
                    this.googleAuthenticated(googleAuthData);
                }
            });
            this.klarna.onNativeInitiatedLogin((klarnaAuthData) => {
                if (klarnaAuthData) {
                    ga.method.set(ga.METHOD.KLARNA);
                    ga.trackEvent('user-signup-step-2');

                    this.setState({ isLoading: true });
                    this.klarnaAuthenticated(klarnaAuthData);
                }
            });
        }
    }

    forceFormUpdate() {
        this.forceUpdate();
    }

    funnelStart(funnel) {
        if (this.funnel === funnel) {
            return;
        }

        this.funnel = funnel;
        Axios.post(
            `/register/api/register/swedish-private/funnel-start/${funnel}`
        );
    }

    initializeLocation() {
        switch (window.location.hash) {
            case '#/confirm':
            case '#/apple/pending':
            case '#/google/pending':
            case '#/klarna/pending':
            case '#/facebook/pending':
            case '#/manual-review':
                try {
                    const successfulSubmissionData = JSON.parse(
                        sessionStorage.getItem('successfulSubmissionData')
                    );
                    this.setState({
                        successfulSubmissionData: successfulSubmissionData
                    });
                    this.setStepFromLocationHash();
                } catch (e) {
                    window.location.hash = '#/step/1';
                }
                break;
            case '#/step/2':
                this.setState({ isLoading: true });
                this.state.form.waitForValidation(() => {
                    this.returnToFirstInvalidStep();
                    this.setStepFromLocationHash();
                    this.setState({ isLoading: false });
                });
                break;
            default:
                this.state.form.onFirstAttempt(() =>
                    this.funnelStart(FUNNEL_PASSWORD)
                );
                window.location.hash = '#/step/1';
                break;
        }
        window.addEventListener('hashchange', this.setStepFromLocationHash);
    }

    setStepFromLocationHash() {
        this.setState({ step: window.location.hash.replace('#', '') });
        GoogleTagManager.trackPageView();
    }

    componentWillUnmount() {
        this.state.form.removeOnChange(this.forceFormUpdate);
        this.state.form.removeOnChange(this.formStore.saveState);
        this.autoFill.deregister(this.autoFill.onValidation);

        window.removeEventListener('hashchange', this.setStepFromLocationHash);
    }

    render() {
        return (
            <>
                {this.currentStep()}
                {this.state.isLoading && <BlockingLoader />}
            </>
        );
    }

    currentStep() {
        const successData = this.state.successfulSubmissionData || {};

        switch (this.state.step) {
            case '/step/1':
            default:
                return (
                    <SwedishPrivateFormStep1
                        form={this.state.form}
                        {...this.props}
                        onNavigate={() => this.step2()}
                        onFacebookStart={() => this.facebookStart()}
                        facebookNotLoaded={this.state.facebookNotLoaded}
                        onAppleStart={() => this.appleStart()}
                        appleNotLoaded={this.state.appleNotLoaded}
                        onGoogleStart={(providerData) =>
                            this.googleStart(providerData)
                        }
                        googleNotLoaded={this.state.googleNotLoaded}
                        onKlarnaStart={(providerData) =>
                            this.klarnaStart(providerData)
                        }
                        klarnaNotLoaded={this.state.klarnaNotLoaded}
                        isNativeAppContext={this.props.isNativeAppContext}
                    />
                );
            case '/step/2':
                return (
                    <SwedishPrivateFormStep2
                        form={this.state.form}
                        {...this.props}
                        onNavigate={() => this.submit()}
                        onChangeAcceptsTerms={(v) =>
                            this.setState({
                                acceptsTerms: v,
                                termsError: false
                            })
                        }
                        acceptsTerms={this.state.acceptsTerms}
                        termsError={this.state.termsError}
                    />
                );
            case '/confirm':
                return (
                    <SwedishPrivateFormAccountCreated
                        successfulSubmissionData={successData}
                        {...this.props}
                    />
                );
            case '/manual-review':
                return (
                    <SwedishPrivateManualReview
                        successfulSubmissionData={successData}
                        {...this.props}
                    />
                );
            case '/facebook':
                return (
                    <SwedishPrivateFacebook
                        form={this.state.form}
                        {...this.props}
                        onNavigate={() =>
                            this.submitWithProvider(PROVIDERS.FACEBOOK)
                        }
                        onChangeAcceptsTerms={(v) =>
                            this.setState({
                                acceptsTerms: v,
                                termsError: false
                            })
                        }
                        acceptsTerms={this.state.acceptsTerms}
                        termsError={this.state.termsError}
                    />
                );
            case '/facebook/pending':
                return (
                    <SwedishPrivateWaitForAccountCreation
                        successfulSubmissionData={successData}
                        {...this.props}
                        onNavigate={() => this.finishFacebookRegistration()}
                        onManualReview={() => this.manualReview()}
                        onEmailExists={() =>
                            this.emailExists(PROVIDERS.FACEBOOK)
                        }
                    />
                );
            case '/facebook/confirm':
                return (
                    <SwedishPrivateFormAccountCreatedWithProvider
                        isNativeAppContext={this.props.isNativeAppContext}
                        helpUrl={this.props.helpUrl}
                        redirectLink={this.props.redirectLink}
                        firstName={
                            this.facebook.firstName || successData.firstName
                        }
                        provider={PROVIDERS.FACEBOOK}
                        providerName={PROVIDER_NAMES[PROVIDERS.FACEBOOK]}
                        userIcon={
                            <FacebookProfileIcon
                                facebookUserId={
                                    this.facebook.userId ||
                                    successData.facebookId
                                }
                                size={80}
                            />
                        }
                    />
                );
            case '/facebook/id-exists':
                return (
                    <SwedishPrivateProviderIdExists
                        provideproviderName={PROVIDER_NAMES[PROVIDERS.FACEBOOK]}
                        successfulSubmissionData={successData}
                        {...this.props}
                    />
                );
            case '/facebook/email-exists':
                return (
                    <SwedishPrivateProviderEmailExists
                        providerName={PROVIDER_NAMES[PROVIDERS.FACEBOOK]}
                        successfulSubmissionData={successData}
                        {...this.props}
                    />
                );
            case '/facebook/email-required':
                return (
                    <SwedishPrivateProviderEmailRequired
                        providerName={PROVIDER_NAMES[PROVIDERS.FACEBOOK]}
                        successfulSubmissionData={successData}
                        {...this.props}
                    />
                );
            case '/apple':
                return (
                    <SwedishPrivateApple
                        form={this.state.form}
                        {...this.props}
                        onNavigate={() =>
                            this.submitWithProvider(PROVIDERS.APPLE)
                        }
                        onChangeAcceptsTerms={(v) =>
                            this.setState({
                                acceptsTerms: v,
                                termsError: false
                            })
                        }
                        acceptsTerms={this.state.acceptsTerms}
                        termsError={this.state.termsError}
                    />
                );
            case '/apple/pending':
                return (
                    <SwedishPrivateWaitForAccountCreation
                        successfulSubmissionData={successData}
                        {...this.props}
                        onNavigate={() => this.finishAppleRegistration()}
                        onManualReview={() => this.manualReview()}
                        onEmailExists={() => this.emailExists(PROVIDERS.APPLE)}
                    />
                );
            case '/apple/confirm':
                return (
                    <SwedishPrivateFormAccountCreatedWithProvider
                        isNativeAppContext={this.props.isNativeAppContext}
                        helpUrl={this.props.helpUrl}
                        redirectLink={this.props.redirectLink}
                        firstName={
                            this.apple.firstName || successData.firstName
                        }
                        provider={PROVIDERS.APPLE}
                        providerName={PROVIDER_NAMES[PROVIDERS.APPLE]}
                    />
                );
            case '/apple/id-exists':
                return (
                    <SwedishPrivateProviderIdExists
                        providerName={PROVIDER_NAMES[PROVIDERS.APPLE]}
                        successfulSubmissionData={successData}
                        {...this.props}
                    />
                );
            case '/apple/email-exists':
                return (
                    <SwedishPrivateProviderEmailExists
                        providerName={PROVIDER_NAMES[PROVIDERS.APPLE]}
                        successfulSubmissionData={successData}
                        {...this.props}
                    />
                );

            case '/google':
                return (
                    <SwedishPrivateGoogle
                        form={this.state.form}
                        {...this.props}
                        onNavigate={() =>
                            this.submitWithProvider(PROVIDERS.GOOGLE)
                        }
                        onChangeAcceptsTerms={(v) =>
                            this.setState({
                                acceptsTerms: v,
                                termsError: false
                            })
                        }
                        acceptsTerms={this.state.acceptsTerms}
                        termsError={this.state.termsError}
                    />
                );
            case '/google/pending':
                return (
                    <SwedishPrivateWaitForAccountCreation
                        successfulSubmissionData={successData}
                        {...this.props}
                        onNavigate={() => this.finishGoogleRegistration()}
                        onManualReview={() => this.manualReview()}
                        onEmailExists={() => this.emailExists(PROVIDERS.GOOGLE)}
                    />
                );
            case '/google/confirm':
                return (
                    <SwedishPrivateFormAccountCreatedWithProvider
                        isNativeAppContext={this.props.isNativeAppContext}
                        helpUrl={this.props.helpUrl}
                        redirectLink={this.props.redirectLink}
                        firstName={
                            this.google.firstName || successData.firstName
                        }
                        provider={PROVIDERS.GOOGLE}
                        providerName={PROVIDER_NAMES[PROVIDERS.GOOGLE]}
                    />
                );
            case '/google/id-exists':
                return (
                    <SwedishPrivateProviderIdExists
                        providerName={PROVIDER_NAMES[PROVIDERS.GOOGLE]}
                        successfulSubmissionData={successData}
                        {...this.props}
                    />
                );
            case '/google/email-exists':
                return (
                    <SwedishPrivateProviderEmailExists
                        providerName={PROVIDER_NAMES[PROVIDERS.GOOGLE]}
                        successfulSubmissionData={successData}
                        {...this.props}
                    />
                );

            case '/klarna':
                return (
                    <SwedishPrivateKlarna
                        form={this.state.form}
                        {...this.props}
                        onNavigate={() =>
                            this.submitWithProvider(PROVIDERS.KLARNA)
                        }
                        onChangeAcceptsTerms={(v) =>
                            this.setState({
                                acceptsTerms: v,
                                termsError: false
                            })
                        }
                        acceptsTerms={this.state.acceptsTerms}
                        termsError={this.state.termsError}
                    />
                );
            case '/klarna/pending':
                return (
                    <SwedishPrivateWaitForAccountCreation
                        successfulSubmissionData={successData}
                        {...this.props}
                        onNavigate={() => this.finishKlarnaRegistration()}
                        onManualReview={() => this.manualReview()}
                        onEmailExists={() => this.emailExists(PROVIDERS.KLARNA)}
                    />
                );
            case '/klarna/confirm':
                return (
                    <SwedishPrivateFormAccountCreatedWithProvider
                        isNativeAppContext={this.props.isNativeAppContext}
                        helpUrl={this.props.helpUrl}
                        redirectLink={this.props.redirectLink}
                        firstName={
                            this.klarna.firstName || successData.firstName
                        }
                        provider={PROVIDERS.KLARNA}
                        providerName={PROVIDER_NAMES[PROVIDERS.KLARNA]}
                    />
                );
            case '/klarna/id-exists':
                return (
                    <SwedishPrivateProviderIdExists
                        providerName={PROVIDER_NAMES[PROVIDERS.KLARNA]}
                        successfulSubmissionData={successData}
                        {...this.props}
                    />
                );
            case '/klarna/email-exists':
                return (
                    <SwedishPrivateProviderEmailExists
                        providerName={PROVIDER_NAMES[PROVIDERS.KLARNA]}
                        successfulSubmissionData={successData}
                        {...this.props}
                    />
                );
        }
    }

    submit() {
        this.setState({ isLoading: true });
        ga.trackEvent('user-signup-submit');

        this.state.form.waitForValidation(() => {
            if (!this.state.form.isValid()) {
                this.setState({ isLoading: false });
                return;
            }

            this.state.form.trim('Alias');
            this.state.form.trim('EmailAddress');

            waitForCountSessionId()
                .then((sessionId) => {
                    const submissionData = this.state.form.fieldNames().reduce(
                        (formData, field) => ({
                            ...formData,
                            [field]: this.state.form.get(field)
                        }),
                        {
                            terms: this.state.acceptsTerms,
                            sessionId: sessionId
                        }
                    );

                    return Axios.post(this.props.url, submissionData, {
                        validateStatus: false
                    });
                })
                .then((response) => {
                    if (
                        response.status >= 400 &&
                        response.data &&
                        response.data.errors
                    ) {
                        this.setState({ isLoading: false });
                        this.setValidationErrors(response.data.errors);
                        return;
                    }

                    this.formStore.clear();
                    this.setState({
                        successfulSubmissionData: response.data
                    });
                    try {
                        sessionStorage.setItem(
                            'successfulSubmissionData',
                            JSON.stringify(response.data)
                        );
                    } catch (e) {
                        // DO NOTHING
                    }
                    ga.trackEvent(
                        'user-signup-success',
                        response.data.memberId
                    );
                    blueshift.triggerUserSignedUpEvent(
                        response.data.memberId,
                        response.data.emailAddress
                    );
                    window.location.hash = '/confirm';
                    this.setState({ isLoading: false });
                    return;
                })
                .catch((err) => {
                    console.error('Submission (Email/Password) failed', err);
                });
        });
    }

    submitWithProvider(provider) {
        this.setState({ isLoading: true });
        ga.trackEvent('user-signup-submit');

        this.state.form.waitForValidation(() => {
            if (!this.state.form.isValid()) {
                this.setState({ isLoading: false });
                return;
            }

            const submissionData = this.state.form.fieldNames().reduce(
                (formData, field) => ({
                    ...formData,
                    [field]: this.state.form.get(field)
                }),
                { terms: this.state.acceptsTerms }
            );

            Axios.post(this.props.url, submissionData, {
                validateStatus: false
            })
                .then((response) => {
                    if (
                        response.status >= 400 &&
                        response.data &&
                        response.data.errors
                    ) {
                        this.setState({ isLoading: false });
                        this.setValidationErrors(response.data.errors);
                        return;
                    }

                    this.formStore.clear();
                    try {
                        sessionStorage.setItem(
                            'successfulSubmissionData',
                            JSON.stringify(response.data)
                        );
                    } catch (e) {
                        //DO NOTHING
                    }

                    this.setState(
                        {
                            successfulSubmissionData: response.data,
                            isLoading: false
                        },
                        () => (window.location.hash = `/${provider}/pending`)
                    );
                    return;
                })
                .catch((err) => {
                    console.error(
                        `Submission (${PROVIDER_NAMES[provider]}) failed`,
                        err
                    );
                });
        });
    }

    loginWithFacebook() {
        Axios.post('/login/facebook/verify', {
            AccessToken: this.state.form.get('FacebookUserAccessToken'),
            UserId: this.state.form.get('FacebookId')
        })
            .then((res) => {
                ga.trackEvent(
                    'user-login-success',
                    res && res.data && res.data.memberId
                );
                this.setState({ isLoading: false });
                window.location.hash = '/facebook/confirm';
                return;
            })
            .catch((err) => {
                ga.trackEvent(
                    'user-login-fail',
                    null,
                    err && err.data && err.data.error
                );
                this.setState({ isLoading: false });
                console.error('/login/facebook/verify failed', err);
                window.location.hash = '/facebook/confirm';
            });
    }

    loginOrPairWithApple() {
        Axios.post('/login/apple/verify', {
            AccessToken: this.state.form.get('AppleUserAccessToken'),
            UserId: 'NOT_IN_USE'
        })
            .then((res) => {
                ga.trackEvent(
                    'user-login-success',
                    res && res.data && res.data.memberId
                );
                window.location.hash = '/apple/confirm';
                return;
            })
            .catch(() => {
                this.pairExistingAccountWithApple();
            });
    }

    pairExistingAccountWithApple() {
        Axios.post('/login/apple/verify', {
            AccessToken: this.state.form.get('AppleUserAccessToken'),
            UserId: 'NOT_IN_USE'
        })
            .then((res) => {
                ga.trackEvent(
                    'user-login-through-registration-success',
                    res && res.data && res.data.memberId
                );
                window.location.href =
                    '/login?connect=apple&connectmatch=email';
                return;
            })
            .catch((err) => {
                ga.trackEvent(
                    'user-login-through-registration-fail',
                    null,
                    err && err.data && err.data.error
                );
                window.location.href =
                    '/login?connect=apple&connectmatch=email';
            });
    }

    loginOrPairWithGoogle() {
        Axios.post('/login/google/verify', {
            AccessToken: this.state.form.get('GoogleUserAccessToken'),
            UserId: 'NOT_IN_USE'
        })
            .then((res) => {
                ga.trackEvent(
                    'user-login-success',
                    res && res.data && res.data.memberId
                );
                window.location.hash = '/google/confirm';
                return;
            })
            .catch(() => {
                this.pairExistingAccountWithGoogle();
            });
    }

    loginOrPairWithKlarna() {
        Axios.post('/login/klarna/verify', {
            AccessToken: this.state.form.get('KlarnaIdToken'),
            RefreshToken: this.state.form.get('KlarnaRefreshToken'),
            UserId: this.state.form.get('KlarnaId')
        })
            .then((res) => {
                ga.trackEvent(
                    'user-login-success',
                    res && res.data && res.data.memberId
                );
                window.location.hash = '/klarna/confirm';
                return;
            })
            .catch((err) => {
                if (err.response?.data?.returnUrl) {
                    window.location.href = err.response.data.returnUrl;
                } else {
                    this.pairExistingAccountWithKlarna();
                }
            });
    }

    pairExistingAccountWithGoogle() {
        Axios.post('/login/google/verify', {
            AccessToken: this.state.form.get('GoogleUserAccessToken'),
            UserId: 'NOT_IN_USE'
        })
            .then((res) => {
                ga.trackEvent(
                    'user-login-through-registration-success',
                    res && res.data && res.data.memberId
                );
                window.location.href =
                    '/login?connect=google&connectmatch=email';
                return;
            })
            .catch((err) => {
                ga.trackEvent(
                    'user-login-through-registration-fail',
                    null,
                    err && err.data && err.data.error
                );
                window.location.href =
                    '/login?connect=google&connectmatch=email';
            });
    }

    pairExistingAccountWithKlarna() {
        Axios.post('/login/klarna/verify', {
            AccessToken: this.state.form.get('KlarnaIdToken'),
            RefreshToken: this.state.form.get('KlarnaRefreshToken'),
            UserId: this.state.form.get('KlarnaId')
        })
            .then((res) => {
                ga.trackEvent(
                    'user-login-through-registration-success',
                    res && res.data && res.data.memberId
                );
                window.location.href =
                    '/login?connect=klarna&connectmatch=email';
                return;
            })
            .catch((err) => {
                ga.trackEvent(
                    'user-login-through-registration-fail',
                    null,
                    err && err.data && err.data.error
                );
                window.location.href =
                    '/login?connect=klarna&connectmatch=email';
            });
    }

    finishFacebookRegistration() {
        const successData = this.state.successfulSubmissionData || {};
        ga.trackEvent('user-signup-success', successData.memberId);
        blueshift.triggerUserSignedUpEvent(
            successData.memberId,
            successData.emailAddress
        );

        Axios.post('/login/facebook/verify', {
            AccessToken: this.state.form.get('FacebookUserAccessToken'),
            UserId: this.state.form.get('FacebookId')
        })
            .then(() => {
                window.location.hash = '/facebook/confirm';
                return;
            })
            .catch((err) => {
                window.location.hash = '/facebook/confirm';
                console.error('/login/facebook/verify failed', err);
            });
    }

    finishAppleRegistration() {
        const successData = this.state.successfulSubmissionData || {};
        ga.trackEvent('user-signup-success', successData.memberId);
        blueshift.triggerUserSignedUpEvent(
            successData.memberId,
            successData.emailAddress
        );

        Axios.post('/login/apple/verify', {
            AccessToken: this.state.form.get('AppleUserAccessToken'),
            UserId: 'NOT_IN_USE'
        })
            .then(() => {
                window.location.hash = '/apple/confirm';
                return;
            })
            .catch((err) => {
                window.location.hash = '/apple/confirm';
                console.error('/login/apple/verify failed', err);
            });
    }

    finishGoogleRegistration() {
        const successData = this.state.successfulSubmissionData || {};
        ga.trackEvent('user-signup-success', successData.memberId);
        blueshift.triggerUserSignedUpEvent(
            successData.memberId,
            successData.emailAddress
        );

        Axios.post('/login/google/verify', {
            AccessToken: this.state.form.get('GoogleUserAccessToken'),
            UserId: 'NOT_IN_USE'
        })
            .then(() => {
                window.location.hash = '/google/confirm';
                return;
            })
            .catch((err) => {
                window.location.hash = '/google/confirm';
                console.error('/login/google/verify failed', err);
            });
    }

    finishKlarnaRegistration() {
        const successData = this.state.successfulSubmissionData || {};
        ga.trackEvent('user-signup-success', successData.memberId);
        blueshift.triggerUserSignedUpEvent(
            successData.memberId,
            successData.emailAddress
        );

        Axios.post('/login/klarna/verify', {
            AccessToken: this.state.form.get('KlarnaIdToken'),
            RefreshToken: this.state.form.get('KlarnaRefreshToken'),
            UserId: this.state.form.get('KlarnaId')
        })
            .then(() => {
                window.location.hash = '/klarna/confirm';
                return;
            })
            .catch((err) => {
                window.location.hash = '/klarna/confirm';
                console.error('/login/klarna/verify failed', err);
            });
    }

    manualReview() {
        const successData = this.state.successfulSubmissionData || {};
        ga.trackEvent(
            'user-signup-success',
            successData.memberId,
            'credit-safe'
        );
        blueshift.triggerUserSignedUpEvent(
            successData.memberId,
            successData.emailAddress
        );
        window.location.hash = '/manual-review';
    }

    emailExists(provider) {
        ga.trackEvent('user-signup-fail', 0, 'email-exists');
        window.location.hash = `/${provider}/email-exists`;
    }

    setValidationErrors(errors) {
        errors
            .filter(({ code }) => code !== 'REQUIRED_TERMS')
            .forEach((error) => {
                this.state.form.addError(error.field, {
                    code: error.code,
                    description: ErrorMessages['sv'][error.code]
                });
            });

        const termsError = errors.find(({ code }) => code === 'REQUIRED_TERMS');

        if (termsError) {
            this.setState({ termsError: true });
        }

        this.returnToFirstInvalidStep();
    }

    returnToFirstInvalidStep() {
        if (!this.state.form.isValid(['EmailAddress', 'PersonalNumber'])) {
            window.location.hash = '';
        }
    }

    step2() {
        const fields = ['EmailAddress', 'PersonalNumber'];
        this.setState({ isLoading: true });
        this.state.form.waitForValidation(() => {
            this.setState({ isLoading: false });
            if (!this.state.form.isValid(fields)) {
                return;
            }

            ga.method.set(ga.METHOD.PASSWORD);
            ga.trackEvent('user-signup-step-2');
            window.location.hash = '/step/2';
        }, fields);
    }

    facebookStart() {
        if (!this.facebook.isLoaded() && !this.props.isNativeAppContext) {
            this.setState({ facebookNotLoaded: true });
            ga.trackEvent('user-signup-facebook-error');
            return;
        }
        ga.method.set(ga.METHOD.FACEBOOK);
        ga.trackEvent('user-signup-step-2');

        this.setState({ isLoading: true });
        this.facebook
            .login({ native: this.props.isNativeAppContext })
            .then((result) => {
                const auth = this.facebookAuthenticated(result);
                return auth;
            })
            .catch((err) => {
                console.error('Failed logging in to Facebook', err);
                this.setState({ isLoading: false });
            });
    }

    appleStart() {
        if (!this.apple.isLoaded() && !this.props.isNativeAppContext) {
            this.setState({ appleNotLoaded: true });
            ga.trackEvent('user-signup-apple-error');
            return;
        }
        ga.method.set(ga.METHOD.APPLE);
        ga.trackEvent('user-signup-step-2');

        this.setState({ isLoading: true });
        this.apple
            .login({ native: this.props.isNativeAppContext })
            .then((result) => {
                const auth = this.appleAuthenticated(result);
                return auth;
            })
            .catch((err) => {
                console.error('Failed logging in to Apple', err);
                this.setState({ isLoading: false });
            });
    }

    async googleStart(providerData) {
        if (!this.google.isLoaded() && !this.props.isNativeAppContext) {
            this.setState({ googleNotLoaded: true });
            ga.trackEvent('user-signup-google-error');
            return;
        }
        ga.method.set(ga.METHOD.GOOGLE);
        ga.trackEvent('user-signup-step-2');

        this.setState({ isLoading: true });
        try {
            const result = await this.google.login({
                native: this.props.isNativeAppContext,
                providerData
            });
            this.googleAuthenticated(result);
        } catch (err) {
            console.error('Failed logging in to Google', err);
        } finally {
            this.setState({ isLoading: false });
        }
    }

    async klarnaStart() {
        ga.method.set(ga.METHOD.KLARNA);
        ga.trackEvent('user-signup-step-2');

        this.setState({ isLoading: true });
        try {
            if (!this.props.isNativeAppContext) {
                await window.Klarna.SignIn.onButtonClick();
            } else {
                const result = await this.klarna.login({ native: true });
                this.klarnaAuthenticated(result);
            }
        } catch (err) {
            console.error('Failed logging in to Klarna', err);
        } finally {
            this.setState({ isLoading: false });
        }
    }

    facebookAuthenticated(
        data = { userId: '', accessToken: '', email: '', firstName: '' }
    ) {
        if (!data.email) {
            this.setState({ isLoading: false });
            window.location.hash = '/facebook/email-required';
            ga.trackEvent('user-signup-fail', 0, 'email-is-required-from-fb');
            return;
        }

        this.state.form.setFromGenerated('EmailAddress', data.email);
        this.state.form.setFromGenerated('FirstName', data.firstName);
        this.state.form.setFromGenerated('FacebookId', data.userId);
        this.state.form.setFromGenerated(
            'FacebookUserAccessToken',
            data.accessToken
        );
        this.state.form.setFromGenerated('FacebookFirstName', data.firstName);

        const onValidatedCallback = () => {
            if (this.state.form.hasError('FacebookId', 'FACEBOOK_ID_IN_USE')) {
                this.loginWithFacebook();
                return;
            }
            if (this.state.form.hasError('EmailAddress', 'EMAIL_IN_USE')) {
                window.location.href =
                    '/login?connect=facebook&connectmatch=email';
                return;
            }

            this.setState({ isLoading: false });
            this.funnelStart(FUNNEL_FACEBOOK);
            ga.trackEvent('user-signup-step-3');
        };

        window.location.hash = '/facebook';
        this.state.form.waitForValidation(onValidatedCallback, [
            'EmailAddress',
            'FacebookId'
        ]);
    }

    appleAuthenticated(data = { accessToken: '', email: '', firstName: '' }) {
        if (!data.email) {
            this.setState({ isLoading: false });
            window.location.href = '/login?connect=apple&connectmatch=email';
            return;
        }

        this.state.form.setFromGenerated('EmailAddress', data.email);
        this.state.form.setFromGenerated('FirstName', data.firstName);
        this.state.form.setFromGenerated(
            'AppleUserAccessToken',
            data.accessToken
        );

        const onValidatedCallback = () => {
            if (this.state.form.hasError('EmailAddress', 'EMAIL_IN_USE')) {
                this.setState({ isLoading: false });
                this.loginOrPairWithApple();
                return;
            }

            window.location.hash = '/apple';

            this.setState({ isLoading: false });
            this.funnelStart(FUNNEL_APPLE);
            ga.trackEvent('user-signup-step-3');
        };

        this.state.form.waitForValidation(onValidatedCallback, [
            'EmailAddress'
        ]);
    }

    googleAuthenticated(data = { accessToken: '', email: '', firstName: '' }) {
        if (!data.email) {
            this.setState({ isLoading: false });
            window.location.href = '/login?connect=google&connectmatch=email';
            return;
        }

        this.state.form.setFromGenerated('EmailAddress', data.email);
        this.state.form.setFromGenerated('FirstName', data.firstName);
        this.state.form.setFromGenerated(
            'GoogleUserAccessToken',
            data.accessToken
        );
        this.state.form.setFromGenerated('GoogleFirstName', data.firstName);

        const onValidatedCallback = () => {
            if (this.state.form.hasError('EmailAddress', 'EMAIL_IN_USE')) {
                this.setState({ isLoading: false });
                this.loginOrPairWithGoogle();
                return;
            }

            window.location.hash = '/google';

            this.setState({ isLoading: false });
            this.funnelStart(FUNNEL_GOOGLE);
            ga.trackEvent('user-signup-step-3');
        };
        this.state.form.waitForValidation(onValidatedCallback, [
            'EmailAddress'
        ]);
    }

    klarnaAuthenticated(
        data = {
            accessToken: '',
            refreshToken: '',
            userId: '',
            email: '',
            personalNumber: '',
            firstName: '',
            lastName: '',
            town: '',
            streetName: '',
            zipCode: '',
            phoneNumber: ''
        }
    ) {
        if (!data.email) {
            this.setState({ isLoading: false });
            window.location.href = '/login?connect=klarna&connectmatch=email';
            return;
        }

        this.state.form.setFromGenerated('EmailAddress', data.email);
        this.state.form.setFromGenerated('KlarnaIdToken', data.accessToken);
        this.state.form.setFromGenerated(
            'KlarnaRefreshToken',
            data.refreshToken
        );
        this.state.form.setFromGenerated('KlarnaId', data.userId);

        if (data.personalNumber)
            this.state.form.setFromGenerated(
                'PersonalNumber',
                data.personalNumber
            );
        if (data.firstName)
            this.state.form.setFromGenerated('FirstName', data.firstName);
        if (data.lastName)
            this.state.form.setFromGenerated('LastName', data.lastName);
        if (data.town) this.state.form.setFromGenerated('Town', data.town);
        if (data.streetName)
            this.state.form.setFromGenerated('StreetName', data.streetName);
        if (data.zipCode)
            this.state.form.setFromGenerated('ZipCode', data.zipCode);
        if (data.phoneNumber)
            this.state.form.setFromGenerated('PhoneNumber', data.phoneNumber);

        const onValidatedCallback = () => {
            if (this.state.form.hasError('EmailAddress', 'EMAIL_IN_USE')) {
                this.setState({ isLoading: false });
                this.loginOrPairWithKlarna();
                return;
            }

            window.location.hash = '/klarna';

            this.setState({ isLoading: false });
            this.funnelStart(FUNNEL_KLARNA);
            ga.trackEvent('user-signup-step-3');
        };
        this.state.form.waitForValidation(onValidatedCallback, [
            'EmailAddress'
        ]);
    }
}
